<template>
  <form class="needs-validation ml-0" @submit.prevent="completeForm" novalidate>
    <button class="sr-only" aria-hidden="true" @click="calculating = true;calculate()">Calculate</button>
    <div class="col px-0 pr-lg-3">
      <section class="bg-white rounded mb-4 p-3 p-sm-4">
        <div class="d-flex justify-content-between align-items-start mb-4">
          <h1 class="h2 mb-3">{{edit ? 'Edit' : 'Create' }} Event</h1>
        </div>
        <p v-if="details && details.draftIsFinal"><strong>This draft is marked as the National Finals</strong></p>
        <div role="alert" class="alert alert-warning" v-if="draftZoneId !== '' && draftZoneId === null">Please fill in the zone field in the draft</div>
        <div role="alert" class="alert alert-warning" v-if="errors">{{errors}}</div>
        <div class="row align-items-start">
          <div class="col-12 col-md-6 form-group no-gutters pr-xl-4">
            <label for="eventType">Event Type</label>
            <select class="form-control" id="eventType" required v-model="eventDetails.event_type_id" @change="setName">
              <option disabled></option>
              <option v-for="(type, index) in legacyEventTypes" :key="index" :value="type.id">{{type.name}}</option>
            </select>
            <div class="invalid-tooltip">Please include the Event Type.</div>
          </div>

          <div class="col-12 col-md-6 pl-xl-4 form-group">
            <label for="eventName">Event Name</label>
            <input type="text" class="form-control" id="eventName" required v-model="eventDetails.name">
            <div class="invalid-tooltip">Please include the Event Name.</div>
          </div>
          <div class="col-12 col-md-6 pr-xl-4 not-required">
            <label for="judgeName">Judge</label>
            <judge-search-select
              element-id="judgeName"
              multiple
              :value="judgeIds"
              :initial-options="judgeInitialOptions"
              @input="updateJudgeSelection"
            />
          </div>
          <div class="col-12 col-md-6 pl-xl-4 align-self-end form-group">
            <div class="custom-control custom-checkbox">
              <input type="checkbox" v-model="eventDetails.is_affiliated" class="custom-control-input" id="is_affiliated">
              <label class="custom-control-label font-weight-normal h6 mb-0" for="is_affiliated">Yes, this is an affiliated event</label>
            </div>
          </div>

          <div class="col-12 d-lg-none mt-4">
            <button class="btn btn-block-mob btn-outline-primary" @click.prevent="$nextTick(() => {showRemoveModal = !calculating})" v-if="edit">Remove</button>
            <button :class="['btn btn-block-mob', processing ? 'btn-primary-dark' : 'btn-primary']" @click="calculating = false;handleSubmit()">
              {{ !processing ? edit ? 'Post Points' : 'Create' : '' }}
              <div v-if="processing" class="spinner-border text-light" role="status"><span class="sr-only">Loading...</span></div>
            </button>
          </div>
        </div>
      </section>

      <section class="bg-white rounded mb-4 p-3 p-sm-4">
        <h2 class="mb-sm-3">Results</h2>
        <div class="alert alert-warning" v-if="warningItems.length">
          There are issues with the following results:
          <ul class="mb-0 mt-1">
            <li v-for="(item, i) in warningItems" :key="i">Place {{ item.place }}: <strong>{{ item.reason }}</strong></li>
          </ul>
        </div>
        <div class="row align-items-start">
          <div class="col-12 col-md-auto form-group no-gutters pr-xl-4">
            <label for="starters">Starters</label>
            <input type="number" class="form-control" id="starters" v-model="eventDetails.starters" min="0" :required="errorStatus === 'starters'">
            <div class="invalid-tooltip">Please include the number of Starters.</div>
          </div>
        </div>

        <div class="form-row align-items-start" v-for="(result, resultIndex) in eventDetails.results" :key="resultIndex" v-if="result.is_cutout !== 1">
          <div :class="['col-12 col-md form-group', isLegacy() ? 'col-lg-1' : 'flex-grow-0']">
            <label :for="'position' + resultIndex">Place</label>
            <input type="text" class="form-control" :id="'position' + resultIndex" required v-model="eventDetails.results[resultIndex].place" disabled>
          </div>
          <div class="col-12 col-md form-group" style="min-width: 200px;">
            <label :for="'competitor' + resultIndex">Competitor</label>
            <div :id="'competitordiv' + resultIndex" class="not-required">
              <member-search-select
                :element-id="'competitor' + resultIndex"
                context="competitor"
                :value="competitorValue(resultIndex)"
                :initial-options="competitorInitialOptions(resultIndex)"
                @input="v => onCompetitorPicked(resultIndex, v)"
              />
            </div>
          </div>
          <div class="col-12 col-md form-group" style="min-width: 200px;">
            <label :for="'horse' + resultIndex">Horse</label>
            <div :id="'horsediv' + resultIndex" :class="[{ 'not-required' : !eventDetails.results[resultIndex].score }]">
              <horse-search-select
                :element-id="'horse' + resultIndex"
                :value="horseValue(resultIndex)"
                :initial-option="eventDetails.results[resultIndex] && eventDetails.results[resultIndex].horse"
                @input="v => onHorsePicked(resultIndex, v)"
              />
              <div class="invalid-tooltip" :style="isInvalid(resultIndex) && eventDetails.results[resultIndex].score ? 'display: block': ''">Please include the Horse.</div>
            </div>
          </div>
          <div :class="['col-12 col-md form-group', isLegacy() ? 'col-lg-1' : 'flex-grow-0']">
            <label :for="'score' + resultIndex" class="pr-md-4 pr-xl-5">Score</label>
            <input @keydown.enter="calculating = true;" type="text" class="form-control pr-md-2" :id="'score' + resultIndex" :required="eventDetails.results[resultIndex].horse_id" v-model="eventDetails.results[resultIndex].score">
            <div class="invalid-tooltip">Please include the Score.</div>
          </div>
          <div :class="['col-12 col-md form-group', isLegacy() ? 'col-lg-1' : 'flex-grow-0']">
            <label :for="'points' + resultIndex" class="pr-md-4 pr-xl-5">Points</label>
            <input type="text" class="form-control" :id="'points' + resultIndex" required v-model="eventDetails.results[resultIndex].points" disabled>
            <div class="custom-control custom-checkbox mt-1" v-if="eventDetails.event_type_id === 9 || eventDetails.event_type_id === 10">
              <input type="checkbox" class="custom-control-input" :id="'nopoints' + resultIndex" v-model="eventDetails.results[resultIndex].zero_rider_points_override">
              <label class="custom-control-label font-weight-normal text-nowrap" :for="'nopoints' + resultIndex">No Points</label>
            </div>
          </div>
          <div :class="['col-12 col-md form-group pb-lg-1 pl-md-0', isLegacy() ? 'col-lg-1' : 'flex-grow-0']">
            <label aria-hidden="true" class="d-none d-md-block">&nbsp;</label>
            <button @click.prevent="removeResult(resultIndex)" aria-label="Remove Result" class="btn btn-link px-2 pl-md-0 px-xl-2 pr-md-1 mr-n2 d-none d-md-block">
              <div class="btn-remove">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" aria-hidden="true" viewBox="0 0 17 17"><g fill="currentColor"><path d="M8.5 17C3.8 17 0 13.2 0 8.5S3.8 0 8.5 0 17 3.8 17 8.5 13.2 17 8.5 17zm0-16C4.4 1 1 4.4 1 8.5S4.4 16 8.5 16 16 12.6 16 8.5 12.6 1 8.5 1z"/><path d="M10.6 11.1c-.1 0-.3 0-.4-.1L6 6.8c-.2-.2-.2-.5 0-.7s.5-.2.7 0l4.2 4.2c.2.2.2.5 0 .7-.1 0-.2.1-.3.1z"/><path d="M6.4 11.1c-.1 0-.3 0-.4-.1-.2-.2-.2-.5 0-.7l4.2-4.2c.2-.2.5-.2.7 0s.2.5 0 .7L6.7 11s-.2.1-.3.1z"/></g></svg>
              </div>
            </button>
            <button class="btn btn-block-mob btn-outline-primary d-md-none" @click="removeResult(resultIndex)" aria-label="Remove Result">Remove</button>
          </div>
        </div>

        <div class="d-lg-flex justify-content-end mt-3">
          <button class="btn btn-block-mob btn-outline-primary mr-lg-2" @click.prevent="addAnotherResult(false);focusNew()">Add More Results</button>
          <button :class="['btn btn-block-mob', processing3 ? 'btn-primary-dark' : 'btn-primary']" @click="calculating = true;calculate()">
            {{ !processing3 ? 'Calculate' : '' }}
            <div v-if="processing3" class="spinner-border text-light" role="status"><span class="sr-only">Loading...</span></div>
          </button>
        </div>
      </section>

      <section class="bg-white rounded mb-4 p-3 p-sm-4" v-for="(result, resultIndex) in eventDetails.results" :key="resultIndex" v-if="result.is_cutout !== 0">
        <h2 class="mb-sm-3">Cut Out</h2>

        <div class="form-row align-items-start">
          <div class="col-12 col-md form-group" style="min-width: 200px;">
            <label :for="'competitor' + resultIndex">Competitor</label>
            <div :id="'competitordiv' + resultIndex" class="not-required">
              <member-search-select
                :element-id="'competitor' + resultIndex"
                context="competitor"
                :value="competitorValue(resultIndex)"
                :initial-options="competitorInitialOptions(resultIndex)"
                @input="v => onCompetitorPicked(resultIndex, v)"
              />
            </div>
          </div>
          <div class="col-12 col-md form-group" style="min-width: 200px;">
            <label :for="'horse' + resultIndex">Horse</label>
            <div :id="'horsediv' + resultIndex" :class="[{ 'not-required' : !eventDetails.results[resultIndex].score }]">
              <horse-search-select
                :element-id="'horse' + resultIndex"
                :value="horseValue(resultIndex)"
                :initial-option="eventDetails.results[resultIndex] && eventDetails.results[resultIndex].horse"
                @input="v => onHorsePicked(resultIndex, v)"
              />
              <div class="invalid-tooltip" :style="isInvalid(resultIndex) && eventDetails.results[resultIndex].score ? 'display: block': ''">Please include the Horse.</div>
            </div>
          </div>
          <div :class="['col-12 col-md form-group', isLegacy() ? 'col-lg-1' : 'flex-grow-0']">
            <label :for="'score' + resultIndex" class="pr-md-4 pr-xl-5">Score</label>
            <input type="text" min="0" class="form-control pr-md-2" :id="'score' + resultIndex" :required="eventDetails.results[resultIndex].horse_id && eventDetails.results[resultIndex].horse_id !== 0" v-model="eventDetails.results[resultIndex].score">
            <div class="invalid-tooltip">Please include the Score.</div>
          </div>
          <div :class="['col-12 col-md form-group', isLegacy() ? 'col-lg-1' : 'flex-grow-0']">
            <label for="pointsCutout" class="pr-md-4 pr-xl-5">Points</label>
            <input type="number" class="form-control" id="pointsCutout" required v-model="eventDetails.results[resultIndex].points" disabled>
          </div>
        </div>

        <div class="d-flex justify-content-end mt-3">
          <button :class="['btn btn-block-mob', processing3 ? 'btn-primary-dark' : 'btn-primary']" @click="calculating = true;calculate()">
            {{ !processing3 ? 'Calculate' : '' }}
            <div v-if="processing3" class="spinner-border text-light" role="status"><span class="sr-only">Loading...</span></div>
          </button>
        </div>
      </section>
    </div>

    <desktop-action-bar>
      <button class="btn btn-block btn-outline-primary" @click.prevent="$nextTick(() => {showRemoveModal = !calculating})" v-if="edit">Remove</button>
      <button :class="['btn btn-block', processing ? 'btn-primary-dark' : 'btn-primary']" @click="calculating = false;handleSubmit()">
        {{ !processing ? edit ? 'Post Points' : 'Create' : '' }}
        <div v-if="processing" class="spinner-border text-light" role="status"><span class="sr-only">Loading...</span></div>
      </button>
    </desktop-action-bar>

    <!-- use the modal component, pass in the prop -->
    <modal v-if="showChangeModal" @close.prevent="showChangeModal = false">
      <div slot="body">
        <div class="alert alert-warning text-center">
          <strong>Warning!</strong>
          <p class="mb-0" v-if="eventDetails.showResults !== details.showResults">You are about to {{eventDetails.showResults ? 'show' : 'hide'}} the Results. Are you sure?</p>
          <p class="mb-0" v-if="eventDetails.showCutout !== details.showCutout">You are about to {{eventDetails.showCutout ? 'show' : 'hide'}} the Cut Out. Are you sure?</p>
        </div>
      </div>
      <div slot="footer">
        <div class="col-12 d-flex justify-content-center">
          <button class="btn btn-primary" @click.prevent="addEvent()">Confirm</button>
          <button class="btn btn-outline-primary ml-2" @click.prevent="showChangeModal = false">Cancel</button>
        </div>
      </div>
    </modal>
    <modal v-if="showRemoveModal" @close.prevent="showRemoveModal = false">
      <div slot="body">
        <div class="alert alert-warning text-center">
          <strong>Warning!</strong>
          <p>Removing an event will cause associated points and scores to also be removed. You are about to remove this event. Are you sure?</p>
        </div>
      </div>
      <div slot="footer">
        <div class="col-12 d-flex justify-content-center">
          <button :class="['btn', processing2 ? 'btn-primary-dark' : 'btn-primary']" @click.prevent="removeEvent()">
            {{ !processing2 ? 'Confirm' : '' }}
            <div v-if="processing2" class="spinner-border text-light" role="status"><span class="sr-only">Loading...</span></div>
          </button>
          <button class="btn btn-outline-primary ml-2" @click.prevent="showRemoveModal = false">Cancel</button>
        </div>
      </div>
    </modal>
  </form>
</template>

<script>
import axios from 'axios'
import HANDLESUBMIT from '@/mixins/formValidation.js'
import LEGACYBROWSERS from '@/mixins/legacyBrowsers.js'
import modal from '@/components/Modal'
import DesktopActionBar from '@/components/DesktopActionBar'
import HorseSearchSelect from '@/components/Horses/HorseSearchSelect'
import MemberSearchSelect from '@/components/Members/MemberSearchSelect'
import JudgeSearchSelect from '@/components/Judges/JudgeSearchSelect'

// One shared reference for "no horse selected" rows so the horse-search-select `:value`
// prop keeps the same identity across renders.
const EMPTY_SELECTION = Object.freeze([])

export default {
  name: 'EventCreateDetails',
  props: ['details', 'edit'],
  mixins: [HANDLESUBMIT, LEGACYBROWSERS],
  components: {
    'horse-search-select': HorseSearchSelect,
    'member-search-select': MemberSearchSelect,
    'judge-search-select': JudgeSearchSelect,
    modal,
    DesktopActionBar
  },
  data() {
    return {
      eventDetails: (this.details && this.edit) ? { ...this.details, results: [] } : { results: [] },
      resultPages: 1,
      fileName: '',
      // Pre-resolved {id, label} for the already-assigned judge(s), built once from
      // eventDetails.judges[i].user (see setSelectedJudges()) so JudgeSearchSelect shows the
      // chip immediately, without a hydration request. eventDetails.judges itself is
      // overwritten to a plain array of ids by updateJudgeSelection() (the shape
      // /admin/event-edit expects), so the full objects have to live here instead.
      judgeInitialOptions: [],
      selectedCompetitor: [],
      selectedHorse: [],
      errorStatus: '',
      errors: '',
      warningItems: [],
      showChangeModal: false,
      showRemoveModal: false,
      processing: false,
      processing2: false,
      processing3: false,
      calculating: false,
      draftZoneId: '',
      // True while results are being hydrated from the API. Hydration sets selectedHorse /
      // selectedCompetitor directly (not through onHorsePicked/onCompetitorPicked), so this
      // mostly guards against a stray focusOnHorse() call landing mid-hydration — kept because
      // focusing N vue-multiselect search inputs in one tick makes their activate()/focus()
      // bounce between each other and lock the tab (see MemberSearchSelect/HorseSearchSelect).
      mountingResults: false
    }
  },
  computed: {
    // "No Horse Required" (id 22) is not supported by this legacy form — it never gets
    // draws/rounds/horse fields, which this page assumes every event type has.
    // Excluded here rather than fixed, per CAM-1392 (this page is legacy and not maintained).
    legacyEventTypes() {
      return this.$store.state.listEventTypes.filter(type => type.id !== 22)
    },
    judgeIds() {
      return this.eventDetails.judges || []
    }
  },
  watch: {
    selectedCompetitor() {
      this.selectedCompetitor.forEach((result, index) => {
        if (this.eventDetails.results[index].rider_id !== undefined) {
          this.eventDetails.results[index].rider_id = Number(this.selectedCompetitor[index].toString())
        }
      })
    },
    selectedHorse() {
      this.selectedHorse.forEach((result, index) => {
        if (this.eventDetails.results[index].horse_id !== undefined) {
          this.eventDetails.results[index].horse_id = this.selectedHorse[index].length > 0 ? Number(this.selectedHorse[index].toString()) : null
        }
      })
    }
  },
  methods: {
    addAnotherResult(is_cutout) {
      this.eventDetails.results.push({
        rider_id: {},
        horse_id: null,
        score: null,
        points: null,
        position: null,
        is_cutout: is_cutout ? 1 : 0
      })
    },
    // The competitor field only renders an <input> while focused (member-search-select — see
    // its freeze-prevention notes), so a freshly-added or just-calculated row may have none yet;
    // guard the same way focusOnHorse() already does for the horse field below.
    focusNew() {
      setTimeout(() => {
        const input = document.body.querySelector(`#competitordiv${this.eventDetails.results.length - 1} input`)
        if (input) input.focus()
      }, 10)
    },
    focusOnHorse(index) {
      if (this.mountingResults) return
      // Only jump focus into an empty Horse field — onCompetitorPicked() calls this on every
      // pick, including one made on a row that already has a horse; re-focusing there would
      // just steal focus mid-edit for no reason.
      if (this.selectedHorse[index] && this.selectedHorse[index].length) return
      // HorseSearchSelect only renders its <input> while active, so this always finds
      // nothing and silently no-ops - focus its always-present .multiselect root instead,
      // which triggers HorseSearchSelect's own focusin handling to activate + focus it.
      const target = document.body.querySelector(`#horsediv${index} .multiselect`)
      if (this.eventDetails.results.length > 0 && target) {
        this.$nextTick(() => {
          target.focus()
        })
      }
    },
    focusOnScore(index) {
      if (this.eventDetails.results.length > 0 && document.body.querySelector(`#score${index}`) !== null) {
        this.$nextTick(() => {
          document.body.querySelector(`#score${index}`).focus()
        })
      }
    },
    onHorsePicked(index, value) {
      this.$set(this.selectedHorse, index, Array.isArray(value) ? value : [])
      if (value && value.length) this.focusOnScore(index)
    },
    // Stable empty array for unset rows — never `selectedHorse[i] || []` inline in the
    // template (a fresh `[]` every render churns the horse-search-select prop).
    horseValue(index) {
      return this.selectedHorse[index] || EMPTY_SELECTION
    },
    onCompetitorPicked(index, value) {
      this.$set(this.selectedCompetitor, index, Array.isArray(value) ? value : [])
      if (value && value.length) this.focusOnHorse(index)
    },
    // Stable empty array for unset rows — same reasoning as horseValue() above.
    competitorValue(index) {
      return this.selectedCompetitor[index] || EMPTY_SELECTION
    },
    // getEventDetails() already embeds the result's rider (id, first_name, last_name,
    // member_number) precisely so this field never needs a per-row /v2/members/options?ids=
    // lookup just to show the chip on an existing result — see MemberSearchSelect's freeze
    // checklist item on hydration.
    competitorInitialOptions(index) {
      const rider = this.eventDetails.results[index] && this.eventDetails.results[index].rider
      if (!rider) return EMPTY_SELECTION
      return [{ id: rider.id, label: `${rider.member_number}: ${rider.first_name} ${rider.last_name}` }]
    },
    removeResult(index) {
      // this.$delete(this.eventDetails.results, index)
      this.eventDetails.results.splice(index, 1)
      this.selectedCompetitor.splice(index, 1)
      this.selectedHorse.splice(index, 1)
    },
    isInvalid(index) {
      if (this.wasValidated) {
        if (this.eventDetails.results[index].horse_id === undefined || this.eventDetails.results[index].horse_id === null) {
          return true
        }
      }
      return false
    },
    completeForm(event) {
      event.preventDefault()
      setTimeout(() => {
        if (this.formComplete) {
          if (this.edit && (this.eventDetails.showCutout !== this.details.showCutout || this.eventDetails.showResults !== this.details.showResults)) {
            this.showChangeModal = true
          } else if (!this.calculating) {
            this.addEvent(false)
          }
        }
      }, 10)
    },
    calculate() {
      this.checkValidity()
      setTimeout(() => {
        if (this.formComplete) {
          this.addEvent(true)
        }
      }, 20)
    },
    addEvent(calculating) {
      if (this.draftZoneId === null) {
        // eslint-disable-next-line no-alert
        alert('Please fill in the zone field in the draft')
        return
      }
      this.errorStatus = ''
      this.errors = ''
      this.warningItems = []
      this.eventDetails.draft_id = this.eventDetails.draft_id || this.details.id
      if (calculating) {
        this.processing3 = true
      } else {
        this.processing = true
      }
      axios.post(`${this.$api}/admin/event-edit`, this.eventDetails).then((response) => {
        if (calculating) {
          this.processing3 = false
        } else {
          this.processing = false
        }
        if (response.data.status === 'success') {
          // eslint-disable-next-line
          // console.log(response)
          if (calculating) {
            const tempData = response.data.data
            tempData.results = tempData.results.sort((a, b) => ((a.place > b.place) ? 1 : ((b.place > a.place) ? -1 : 0)))
            if (tempData.results.filter(result => result.is_cutout === 1).length === 0) {
              tempData.results.push({
                rider_id: {},
                horse_id: null,
                score: null,
                points: null,
                is_cutout: 1
              })
            }
            tempData.results.push({
              position: null,
              rider_id: {},
              horse_id: null,
              score: null,
              points: null,
              is_cutout: 0
            })
            this.eventDetails = tempData
            this.mountingResults = true
            tempData.results.forEach((result, index) => {
              if (result.horse_id !== undefined && result.horse_id !== null) {
                this.$set(this.selectedHorse, index, [result.horse_id.toString()])
                this.selectedCompetitor[index] = [result.rider_id.toString()]
              } else {
                this.selectedHorse.splice(index, 1)
                this.selectedCompetitor.splice(index, 1)
              }
            })
            this.$nextTick(() => { this.mountingResults = false })
            if (this.eventDetails.judges) {
              this.setSelectedJudges(this.eventDetails.judges)
            }
            this.$nextTick(() => {
              const input = document.body.querySelector(`#competitordiv${this.eventDetails.results.length - 1} input`)
              if (input) input.focus()
            })
            this.warningItems = response.data.warnings || []
            this.calculating = false
            this.formComplete = false
            this.removeValidation()
            this.focusNew()
          } else {
            this.$router.push({ name: 'Events', params: { draftId: this.$route.params.draftId, success: `Great! Your event has been successfully ${this.edit ? 'edited' : 'created'}.` } })
          }
        } else if (response.data.status === 'invalid_judge' && response.data.errors) {
          this.errors = 'The status of the judge should be open'
        } else {
          if (calculating) {
            this.processing3 = false
          } else {
            this.processing = false
          }
          if (response.data.errors && Object.keys(response.data.errors).length > 0) {
            this.errorStatus = 'starters'
            const errorKey = Object.keys(response.data.errors)[0]
            this.errors = errorKey === 'starters' ? 'Please include the number of Starters.' : response.data.errors[errorKey][0]
          }
        }
      })
    },
    removeEvent() {
      this.processing2 = true
      axios.post(`${this.$api}/admin/event-delete`, { id: this.eventDetails.id }).then((response) => {
        this.processing2 = false
        if (response.data.status === 'success') {
          this.$router.push({ name: 'Events', params: { draftId: this.$route.params.draftId, success: `Your event has been successfully removed.` } })
        } else {
          this.processing2 = false
          this.errors = 'Unable to receive data from the server. Please reload the page.'
        }
      })
    },
    fileUpload(event) {
      const uploadedFile = event.target.files[0]
      if (uploadedFile.name.endsWith('.pdf')) {
        this.eventDetails.file = uploadedFile
        this.fileName = uploadedFile.name
      } else {
        delete this.eventDetails.file
        this.fileName = ''
        this.errorStatus = 'filetype_pdf'
      }
    },
    setName() {
      if (!this.eventDetails.name) {
        this.eventDetails.name = this.$store.state.listEventTypes[this.eventDetails.event_type_id - 1].name
      }
    },
    // eventDetails.judges (from admin/event-details/{id}) already embeds each judge's `user`
    // relation — build the same {id, label} shape /v2/judges/options returns directly from
    // that, no extra request needed to show the already-assigned judge(s) as chips. Also
    // resets eventDetails.judges itself to the plain id array /admin/event-edit expects.
    setSelectedJudges(judgesList) {
      this.judgeInitialOptions = (judgesList || []).map(judge => ({
        id: judge.id,
        label: judge.user
          ? `${judge.user.member_number || ''}: ${judge.user.first_name} ${judge.user.last_name}`.replace(/^: /, '')
          : ''
      }))
      this.eventDetails.judges = this.judgeInitialOptions.map(j => String(j.id))
    },
    updateJudgeSelection(ids) {
      this.eventDetails.judges = ids
    }
  },
  mounted() {
    if (!this.details) {
      this.$router.push({ name: 'Events', params: { draftId: this.$route.params.draftId } })
    } else if (this.details.id && this.edit) {
      axios.get(`${this.$api}/admin/event-details/${this.details.id}`).then((response) => {
        if (response.data.status === 'success') {
          this.eventDetails = response.data.data
          if (this.eventDetails.draft && this.eventDetails.draft.name) {
            this.$store.commit('updatePlaceholder', this.eventDetails.draft.name)
          }
          this.mountingResults = true
          this.eventDetails.results.forEach((result, index) => {
            this.$set(this.selectedHorse, index, [result.horse_id.toString()])
            this.selectedCompetitor[index] = [result.rider_id.toString()]
          })
          this.$nextTick(() => { this.mountingResults = false })
          if (this.eventDetails.results.length === 0) {
            this.addAnotherResult(false) // Add a non-cutout
            this.addAnotherResult(true) // Add a cutout
          } else if (this.eventDetails.results.filter(result => result.is_cutout === 1).length === 0) {
            this.addAnotherResult(true) // Add a cutout
          }
          if (this.eventDetails.judges) {
            this.setSelectedJudges(this.eventDetails.judges)
          }
        } else {
          this.error = 'Unable to receive data from the server. Please reload the page.'
        }
      }).finally(() => {
        this.draftZoneId = this.eventDetails.draft.zone_id !== null
          ? this.eventDetails.draft.zone_id
          : null
      })
    }

    this.eventDetails.results = this.eventDetails.results || [{
      rider_id: {},
      horse_id: null,
      score: null,
      points: null
    }]

    if (this.eventDetails.results.length === 0) {
      this.addAnotherResult(false) // Add a non-cutout
      this.addAnotherResult(true) // Add a cutout
    }
  }
}
</script>

<style lang="scss" scoped>
.custom-checkbox {
  padding-left: 1.5rem;

  .custom-control-label {
    line-height: 1.75;
    font-size: 0.8125rem;

    &::before,
    &::after {
      left: -1.5rem;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Drafts/Events/Add/EventAddDetails.vue